// Criação de um mapa Leaflet, compartilhada entre a tela de Mapa e o mini-mapa
// da linha expandida.
//
// **Leaflet é dependência empacotada, não CDN** (SPEC §13): a PoC carregava de
// `unpkg.com`, e dependência de CDN em runtime quebra on-premise. O mapa base
// (tiles) continua vindo de servidor externo, e isso é diferente — sem rede o
// mapa fica cinza mas a aplicação funciona.
//
// O hook existe para NÃO duplicar a montagem do Leaflet, e para nascer imune ao
// bug que já foi pago uma vez no `Mapa.tsx`: um callback recriado a cada render
// do pai, se entrar nas dependências do efeito que CRIA o mapa, destrói e recria
// o mapa a cada render — e as camadas desenhadas somem sem nenhum erro no
// console. A defesa é guardar todo callback numa `ref` e deixar o efeito de
// criação dependendo só do NÓ — nenhum callback, nenhuma opção.

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState, type RefCallback } from "react";
import { acharBasemap, BASEMAP_PADRAO, type Basemap } from "./basemaps.js";

// De onde os tiles vêm, e o porquê de cada provedor, está em `basemaps.ts`. A
// lista é fixa no código: não há mais `VITE_ATLAS_TILES`.
const PADRAO = acharBasemap(BASEMAP_PADRAO);

// Contorno de defeito do Leaflet 1.9.4, no renderizador Canvas (`preferCanvas`).
//
// `_updatePaths` chama `_redraw()` síncrono, e `_redraw` zera `_redrawRequest`
// SEM cancelar o quadro que já estava agendado. Ao remover o mapa,
// `_destroyContainer` cancela `null` e apaga o contexto — e o quadro órfão roda
// depois, sobre canvas nenhum: `Cannot read properties of undefined (reading
// 'clearRect')`, dentro de `requestAnimationFrame`, onde nenhuma fronteira de
// erro do React pega. Acontece ao recolher a linha da gaveta logo depois de o
// mini-mapa desenhar, ou ao sair da tela de mapa no meio de uma atualização.
//
// Sem contexto não há o que redesenhar: o canvas já foi destruído. Reproduzido
// em `mapaLeaflet.test.tsx`; ao subir de versão do Leaflet, apagar isto e rodar
// aquele teste diz se ainda é preciso.
type CanvasInterno = { _ctx?: unknown; _redraw: () => void; _atlasContornado?: true };
const canvas = L.Canvas.prototype as unknown as CanvasInterno;
if (!canvas._atlasContornado) {
  const redesenhar = canvas._redraw;
  canvas._redraw = function (this: CanvasInterno) {
    if (!this._ctx) return;
    redesenhar.call(this);
  };
  canvas._atlasContornado = true;
}

type Opcoes = {
  /** Centro e zoom iniciais. O do Brasil inteiro é o default da tela cheia. */
  centro?: [number, number];
  zoom?: number;
  /** Chamado ao terminar de mover/dar zoom, com o bbox `oeste,sul,leste,norte`. */
  aoMover?: (bbox: string) => void;
  /** Chamado com a posição do cursor, para a leitura de coordenadas. */
  aoPassar?: (lat: number, lon: number) => void;
  /** `false` desliga arrasto, zoom e teclado — o mini-mapa é só para VER. */
  interativo?: boolean;
  /**
   * Mapa base. Sem ele, o padrão de `basemaps.ts` — é o caso do mini-mapa.
   *
   * Diferente das outras opções, este É lido fora da criação: trocar o mapa base
   * troca a camada de tiles com o mapa vivo, sem recriá-lo.
   */
  basemap?: Basemap;
};

export type MapaLeaflet = {
  /**
   * Onde montar: `ref` para o `<div>` do mapa.
   *
   * É ref de CALLBACK, e não `useRef`, porque o nó pode aparecer depois do
   * primeiro render — o mini-mapa mostra "Carregando geometria…" até a consulta
   * voltar. Com `useRef` e efeito de dependência vazia, a criação rodava com o nó
   * ausente, saía cedo e nunca mais rodava: o mini-mapa ficava vazio, sem erro.
   */
  divRef: RefCallback<HTMLDivElement>;
  /** A instância, ou `null` enquanto não montou. */
  mapa: L.Map | null;
  /**
   * Incrementa a cada (re)criação do mapa.
   *
   * Quem desenha camadas coloca isto nas dependências do efeito de desenho: sem
   * ele, uma remontagem (troca de tela, expandir/recolher a linha) deixaria o
   * mapa novo sem as camadas, porque o efeito de desenho não teria por que
   * reexecutar. Não é lido no corpo — é gatilho.
   */
  geracao: number;
  /**
   * O mapa base em uso — o declarado, ou o padrão. Quem monta o contêiner lê
   * `inverteNoEscuro` daqui para decidir a classe `inverte-base`, em vez de
   * supor qual é o padrão.
   */
  basemap: Basemap;
};

/**
 * Monta um mapa Leaflet no `<div>` do `divRef`, com a camada do mapa base.
 *
 * NÃO desenha dado — isso é responsabilidade de quem usa, porque a tela cheia
 * desenha três baldes e o mini-mapa desenha uma feição. O que o hook garante é a
 * parte que os dois compartilham e que é fácil de errar: criação única,
 * destruição no desmonte, `invalidateSize` depois do primeiro paint, e os
 * callbacks à prova de recriação.
 */
export function useMapaLeaflet(opcoes: Opcoes = {}): MapaLeaflet {
  // O nó em ESTADO: quando ele aparece (ou é trocado), o efeito de criação
  // reexecuta. `setNo` tem identidade estável, então serve direto como ref.
  const [no, setNo] = useState<HTMLDivElement | null>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const base = useRef<L.TileLayer | null>(null);
  // Qual mapa base está em `base.current`, para não recriar a mesma camada.
  const baseId = useRef<string | null>(null);
  const [geracao, setGeracao] = useState(0);
  const basemap = opcoes.basemap ?? PADRAO;

  // Todos os callbacks numa `ref`, atualizada a cada render SEM reexecutar o
  // efeito de criação. É a linha que impede o bug descrito no topo do arquivo.
  const cb = useRef(opcoes);
  cb.current = opcoes;

  useEffect(() => {
    if (!no) return;
    const interativo = cb.current.interativo !== false;
    const m = L.map(no, {
      center: cb.current.centro ?? [-14.5, -52],
      zoom: cb.current.zoom ?? 4,
      preferCanvas: true,
      // O mini-mapa é para VER, não navegar: sem arrasto, sem zoom, sem foco por
      // teclado. Deixar interativo roubaria o scroll da página e daria um ponto
      // de tabulação por linha expandida.
      dragging: interativo,
      scrollWheelZoom: interativo,
      doubleClickZoom: interativo,
      boxZoom: interativo,
      keyboard: interativo,
      zoomControl: interativo,
      attributionControl: true,
    });
    // A camada base NÃO entra aqui: ela troca em vida, no efeito abaixo, e
    // criá-la nos dois lugares deixaria a primeira para sempre no mapa — com a
    // atribuição dela acumulada no controle.

    if (cb.current.aoPassar) {
      m.on("mousemove", (e) => cb.current.aoPassar?.(e.latlng.lat, e.latlng.lng));
    }
    if (cb.current.aoMover) {
      m.on("moveend", () => {
        const b = m.getBounds();
        cb.current.aoMover?.(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`);
      });
    }

    mapaRef.current = m;
    setGeracao((g) => g + 1);
    // O Leaflet mede o contêiner na criação. Num layout em grade — ou num painel
    // que acabou de expandir — o tamanho final só existe depois do primeiro
    // paint, e sem isto o mapa nasce com dimensão errada e o `fitBounds`
    // degenera num zoom absurdo.
    const quadro = requestAnimationFrame(() => m.invalidateSize());

    return () => {
      // O quadro pendente sai ANTES do `remove()`. Sem isso, recolher a linha no
      // mesmo quadro em que ela abriu chamava `invalidateSize` num mapa já
      // removido, que agenda um redesenho do canvas sem contexto — e lança
      // `Cannot read properties of undefined (reading 'clearRect')` fora do
      // React, onde nenhuma fronteira de erro pega.
      cancelAnimationFrame(quadro);
      // `map.remove()` desfaz os listeners e libera o nó. Sem ele, trocar de
      // tela ou recolher a linha deixaria uma instância órfã por trás — o
      // vazamento que o item de tema também cobriu.
      m.remove();
      mapaRef.current = null;
      base.current = null;
      baseId.current = null;
    };
    // Só o NÓ nas dependências, de propósito: o mapa é criado uma vez por nó.
    // Callbacks e opções que mudam viajam pela `ref` — é o que impede o bug
    // descrito no topo do arquivo.
  }, [no]);

  // Troca do mapa base, com o mapa vivo.
  //
  // Remover e acrescentar a camada preserva centro, zoom e as feições já
  // desenhadas — recriar o mapa custaria o enquadramento que o curador
  // construiu, que é o estado mais caro da tela.
  useEffect(() => {
    // `geracao` não é lida — é gatilho: o mapa recriado precisa da camada base.
    void geracao;
    const m = mapaRef.current;
    if (!m) return;
    // A criação incrementa `geracao`, e isso reexecuta este efeito logo depois
    // de ele ter posto a camada. Sem esta guarda, a camada recém-criada era
    // removida e outra igual posta por cima — o primeiro lote de tiles pedido
    // e jogado fora a cada montagem.
    if (base.current && m.hasLayer(base.current) && baseId.current === basemap.id) return;
    base.current?.remove();
    base.current = L.tileLayer(basemap.url, {
      attribution: basemap.atribuicao,
      // `maxZoom` do MAPA, e `maxNativeZoom` do provedor: acima do último nível
      // que ele serve, o Leaflet amplia o tile em vez de parar de desenhar. Sem
      // isso, trocar de serviço com o mapa fundo no zoom apagaria o mapa base — e
      // some sem erro, o que se lê como "quebrou".
      maxZoom: 19,
      maxNativeZoom: basemap.zoomNativoMaximo,
    }).addTo(m);
    // O `tilePane` já fica abaixo do `overlayPane`, mas a camada acrescentada
    // depois precisa ir para o fundo do próprio pane.
    base.current.bringToBack();
    baseId.current = basemap.id;
  }, [basemap, geracao]);

  return { divRef: setNo, mapa: mapaRef.current, geracao, basemap };
}

/**
 * Desenha uma geometria GeoJSON num mapa e enquadra nela.
 *
 * Compartilhado porque o estilo de uma feição validada/aguardando é o mesmo no
 * mapa grande e no mini. Devolve a camada criada para quem quiser removê-la.
 */
export function desenharFeicao(
  onde: L.Map | L.LayerGroup,
  geometria: unknown,
  opcoes: { cor: string; tracejado?: boolean; enquadrar?: boolean } = { cor: "#2fd4c4" },
): L.GeoJSON {
  const camada = L.geoJSON({ type: "Feature", geometry: geometria, properties: {} } as never, {
    style: {
      color: opcoes.cor,
      weight: 2,
      fillOpacity: 0.3,
      ...(opcoes.tracejado ? { dashArray: "3,3" } : {}),
    },
    pointToLayer: (_p, latlng) =>
      L.circleMarker(latlng, { radius: 6, weight: 2, color: opcoes.cor }),
  }).addTo(onde);

  // Enquadrar só faz sentido num `Map`; num `LayerGroup` (balde do mapa grande)
  // quem enquadra é o chamador, sobre o envelope de todas as feições.
  if (opcoes.enquadrar && onde instanceof L.Map) {
    const b = camada.getBounds();
    if (b.isValid()) onde.fitBounds(b, { padding: [16, 16], maxZoom: 15 });
  }
  return camada;
}
